/**
 * The three means-test strategies the Aged Care Suggestion Engine never considered.
 *
 * WHAT WAS MISSING. The engine varied the deposit nineteen ways and permuted the funding order
 * across asset types — and never once tried the levers an Australian adviser reaches for first in a
 * residential care case. A client sitting on $900,000 of cash was shown twenty ways to split it
 * between a deposit and a daily payment, and no way at all to reduce what the means test counts.
 *
 * All three act on the ASSESSMENT rather than on the care setting, which is why they belong in every
 * module and are applied by the shared appliers in
 * server/src/rest/calculation/suggestionEngine/meansTestStrategies.ts:
 *
 *   LIFETIME ANNUITY — 60% of the purchase price is assessed as an asset (30% from the threshold
 *   day) and 60% of the payments as income. Moving $300,000 out of the bank therefore removes
 *   $120,000 from the assets test while adding only a fraction of the payment to the income test.
 *   Both the Age Pension and the means-tested care fee fall. The trade-off is liquidity: the capital
 *   is committed for life, so this suits a resident whose care is settled and not one who may need
 *   the money back. Proven end-to-end against this engine in
 *   server/tests/rest/calculation/annuityInAgedCareMeansTest.test.ts.
 *
 *   FUNERAL BONDS — exempt from the assets test up to the published limit, per person, and the money
 *   is spent on something most families buy regardless. The cleanest lever and the smallest: the
 *   limit caps how far it can move the assessment.
 *
 *   GIFTING — a gift within the yearly free area leaves the assessment immediately. Above it, the
 *   excess is still counted, and deemed to earn income, for five years under the deprivation rules
 *   even though the money is gone. So the candidates here stay INSIDE the free area: an engine that
 *   ranked a deliberately-deprived gift highly would be recommending a five-year penalty it has no
 *   way of knowing the family wants.
 *
 * WHY THESE AMOUNTS. Each lever is offered at a few sizes rather than one, because the right size
 * depends on where the client sits against the thresholds — and the engine's job is to find that,
 * not to assume it. Amounts that the client cannot fund, or that are too small to move a fee, are
 * filtered out here so they never become a card promising something it cannot deliver.
 */
import { FUNERAL_BOND_LIMIT, GIFTING_LIMIT_ANNUAL } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { annuityPreferenceOf, allowsResidual, allowsGuaranteed } from '../../../shared/src/calculation/annuityPreference.ts';
import { collectLiquidAssetsForSuggestionRad } from './suggestionEngineFunding.ts';
import type { AvailableSuggestionAsset, RadFundingScenario } from './suggestionEngineFunding.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import { annuitySizesFor } from '../strategy/annuitySizes.ts';

/**
 * Below this, a lifetime annuity is not worth the loss of access: providers set minimums around
 * here, and 40% of a smaller sum moves the assessment too little to change a fee band. The server's
 * applier enforces the same floor, so a candidate under it would silently apply nothing.
 */
export const MIN_ANNUITY_CANDIDATE = 50_000;

/*
 * HOW MUCH (owner, 21 Oct 2026 — the village review's decision 11, every module; annuitySizes.ts): up to a
 * quarter of what the deposit leaves, and — where it would lift the Age Pension — the amount that lifts it
 * most, as a plan of its own. Previously fixed sizes of $100k, $200k, $300k and $500k, whatever the client held.
 */
export const AC_ANNUITY_SHARE_CAP_PCT = 25;

/**
 * The two annuities worth putting side by side, because they are the ends of a real trade-off.
 *
 * Centrelink assesses a lifetime annuity at 60% of its purchase price WHATEVER its residual capital
 * value — RCV changes the payment rate, not the assets-test treatment. So both of these deliver the
 * same means-test relief, and they differ in what happens to the capital:
 *
 *   NIL RCV — the highest income, and nothing left for the estate. Right for a client who needs the
 *   income and is not planning around what they leave behind.
 *
 *   FULL RCV — a lower income, and the capital preserved for the estate. Right for a family weighing
 *   care costs against the inheritance, which in this module is most of them.
 *
 * Offering only nil RCV, which is what the engine did, meant every annuity candidate lost the legacy
 * ranking by its full purchase price on day one: the strategy was not being weighed, it was being
 * disqualified. Naming both lets the ranking decide rather than the default.
 */
const ANNUITY_RCV_VARIANTS: Array<{ residualCapital: number; annuityType?: 'guaranteed'; indexation?: true; label: string; note: string }> = [
    {
        residualCapital: 0,
        label: 'nil residual',
        note: 'This version pays the most income and leaves nothing of the purchase price to the estate.',
    },
    /*
     * RISING WITH INFLATION (15 Oct 2026): the nil-residual shape with the payment indexed to CPI each
     * year — Challenger's indexed Liquid Lifetime, UniSuper's Lifetime Income. It starts lower (the
     * sheet's own indexed table prices it) and overtakes the flat payment as the years pass; Centrelink
     * assesses it exactly as the plain shape. Product-agnostic, like the other three.
     */
    {
        residualCapital: 0,
        indexation: true,
        label: 'rising with inflation',
        note: 'This version starts with a lower income that rises with inflation every year, and leaves nothing of the purchase price to the estate.',
    },
    {
        residualCapital: 1,
        label: 'capital returned',
        note: 'This version pays less income, and the purchase price comes back to the estate if death is within the first half of life expectancy, less after that, and nothing beyond it — the most the rules allow while Centrelink still counts only 60% either way.',
    },
    /*
     * THE GUARANTEED SHAPE (27 Sep 2026): an annuity and a life policy bought together, so the full
     * amount comes back to the estate whenever death comes. It pays the least income of the three
     * and, because most of the money buys the policy, earns far less assets-test relief — only the
     * annuity half is counted at 60%. It is offered only where the client is in, or entering,
     * government-subsidised aged care, which in this module is nearly everyone.
     */
    {
        residualCapital: 1,
        annuityType: 'guaranteed',
        label: 'full amount guaranteed',
        note: 'This version pays the least income, and the full purchase price comes back to the estate whenever death comes — a lifetime annuity and a life policy bought together. Only the annuity half earns the 60% assets-test treatment; the policy is counted at what it cost.',
    },
];

/** Funeral bond sizes tried. The published limit is the ceiling, per person. */
const FUNERAL_BOND_AMOUNTS = [FUNERAL_BOND_LIMIT / 2, FUNERAL_BOND_LIMIT];

/**
 * A liquidation plan across the client's own liquid assets, cheapest-to-touch first.
 *
 * Bank before pension before shares: the bank account is already cash (no tax event, no income
 * forgone beyond interest), an account-based pension draw is tax-free at these ages, and selling
 * shares can realise capital gains. The emergency reserve is already deducted from the bank row by
 * `collectLiquidAssetsForSuggestionRad`, so a strategy can never spend the buffer.
 */
function planFunding(assets: AvailableSuggestionAsset[], amount: number): Array<{ type: string; id: unknown; amountToTake: number }> {
    const order = ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares'];
    const sorted = [...assets].sort((a, b) => {
        const ai = order.indexOf(a.type);
        const bi = order.indexOf(b.type);
        return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
    });
    const plan: Array<{ type: string; id: unknown; amountToTake: number }> = [];
    let remaining = amount;
    for (const asset of sorted) {
        if (remaining <= 0) break;
        const take = Math.min(remaining, asset.value);
        if (take <= 0) continue;
        plan.push({ type: asset.type, id: asset.id, amountToTake: take });
        remaining -= take;
    }
    return remaining > 0.5 ? [] : plan; // Cannot fund it in full → not a candidate.
}

const totalAvailable = (assets: AvailableSuggestionAsset[]) => assets.reduce((sum, a) => sum + a.value, 0);

/**
 * How much liquid money a scenario has already committed to the deposit.
 *
 * WITHOUT THIS, EVERY COMBINATION DOUBLE-SPENDS. A "Pay 100% RAD" scenario has already earmarked the
 * client's cash for the room; layering a $300,000 annuity on top would produce a candidate that
 * spends the same dollars twice, and the applier — which liquidates whatever it can find — would
 * quietly fund only part of it while the card kept the name. The means-test lever therefore only
 * gets what the deposit leaves behind.
 */
function committedToRad(scenario: RadFundingScenario): number {
    return Object.values(scenario.radFunding ?? {})
        .flatMap((entries) => (Array.isArray(entries) ? entries : entries ? [entries] : []))
        .reduce((sum, entry) => sum + (Number((entry as { amount?: number }).amount) || 0), 0);
}

export interface MeansTestStrategySpec {
    type: 'annuity' | 'funeralBonds' | 'gifting';
    /** Gifting and funeral bonds. */
    amount?: number;
    /** Annuity. */
    purchaseAmount?: number;
    /** A provider's quoted annual income, replacing the indicative rate for this candidate. */
    incomeOverride?: number | null;
    /**
     * Annuity: the share of the purchase price promised back on death, 0 to 1, before the capital
     * access schedule winds it down (annuityStrategies.ts). Both ends of this are
     * offered because Centrelink's 60% assets-test treatment applies either way — see
     * ANNUITY_RCV_VARIANTS.
     */
    residualCapital?: number;
    /** 'guaranteed' for the annuity-plus-life-policy shape; absent for a plain lifetime annuity. */
    annuityType?: 'guaranteed';
    ownership: string;
    fundingSources: Array<{ type: string; id: unknown; amountToTake: number }>;
    /** One plain sentence for the card, saying what the lever does and what it costs. */
    rationale: string;
}

/*
 * A QUOTE BELONGS TO ONE TYPE AND ONE AMOUNT (guaranteed income review M5, 21 Oct 2026). One "$/yr" used to go to
 * every annuity candidate, so a quote for the $150,000 full-amount-back type was applied to a $300,000 income-only
 * plan as if it paid the same. Now a quote carries the type and amount it was given for; it is applied, scaled
 * pro rata, to other amounts of the SAME type, and every other type stays on our estimate.
 */
export type AnnuityQuoteKind = 'fullBack' | 'moneyBack' | 'incomeOnly' | 'incomeOnlyRising';

export interface ScopedAnnuityQuote {
    /** The yearly income the provider quoted. */
    income: number;
    /** The type it was quoted for. */
    kind: AnnuityQuoteKind;
    /** The amount it was quoted on. */
    amount: number;
}

/** The quote kind of a candidate's annuity (its type, residual and indexation). */
export function annuityQuoteKindOf(a: { annuityType?: unknown; residualCapital?: unknown; indexation?: unknown; indexed?: unknown; residualPercent?: unknown } | null | undefined): AnnuityQuoteKind {
    if (a?.annuityType === 'guaranteed') return 'fullBack';
    const r = a?.residualCapital != null ? Number(a.residualCapital) : Number(a?.residualPercent) / 100;
    if (Number.isFinite(r) && r > 0) return 'moneyBack';
    return a?.indexation === true || a?.indexed === true ? 'incomeOnlyRising' : 'incomeOnly';
}

/** The type, in the words the quote control uses. */
export const ANNUITY_QUOTE_KIND_WORDS: Record<AnnuityQuoteKind, string> = {
    fullBack: 'the guaranteed income that pays the full amount back',
    moneyBack: 'the guaranteed income with money back in the early years',
    incomeOnly: 'the income-only guaranteed income',
    incomeOnlyRising: 'the income-only guaranteed income that rises with prices',
};

/** The income a scoped quote gives a candidate of this kind and amount, or null (our estimate). */
export function quotedIncomeFor(quote: ScopedAnnuityQuote | number | null | undefined, kind: AnnuityQuoteKind, purchaseAmount: number): number | null {
    if (quote == null) return null;
    // An unscoped number (older callers) still reaches every candidate, as before.
    if (typeof quote === 'number') return quote > 0 ? quote : null;
    if (!(quote.income > 0) || !(quote.amount > 0) || quote.kind !== kind) return null;
    return Math.round(quote.income * (purchaseAmount / quote.amount));
}

/**
 * Pair each means-test lever against the deposit levels already generated.
 *
 * PAIRED, NOT STANDALONE. An annuity on its own is a worse answer than an annuity alongside a
 * sensible deposit, and an adviser would never present the two as alternatives — they are
 * complementary, because the deposit is refundable and the annuity is not. So each lever is layered
 * onto a SUBSET of the existing scenarios (the ones an adviser would actually build on) rather than
 * offered as its own isolated card. The subset keeps the candidate count sane: pairing three levers
 * at four sizes against nineteen deposit levels and every funding permutation would be thousands of
 * projections for no extra insight.
 */
export function appendMeansTestStrategyScenarios(
    scenarios: RadFundingScenario[],
    factFind: DraftFactFind,
    /**
     * A provider's quoted annual income, where the client has one.
     *
     * Threaded into every annuity candidate rather than applied to the winner afterwards: a real
     * quote can change WHICH strategy wins, and a ranking done on our indicative rate would then be
     * the wrong ranking (owner, 2026-09-01).
     */
    annuityIncomeOverride: ScopedAnnuityQuote | number | null = null,
): void {
    const assets = collectLiquidAssetsForSuggestionRad(factFind);
    if (assets.length === 0) return;
    const liquid = totalAvailable(assets);
    if (liquid <= 0) return;

    const isPartnered = factFind.adviceFor === 'couple';
    /*
     * The scenarios worth building on: doing nothing (so the lever's own effect is visible against
     * the baseline) and the deposit levels an adviser would actually recommend. Not every level —
     * see the doc comment above.
     */
    /*
     * ANCHORED AT THE START ONLY. A generated name carries a funding-order suffix whenever the
     * client has more than one way to raise the money — "Pay 50% RAD, Keep Home (Bank -> Shares)" —
     * so a pattern anchored at the END matched almost nothing, and the levers were built for a
     * handful of clients and silently skipped for everyone else. Caught by counting the candidates
     * that actually reached the engine, not by reading the code.
     *
     * One base per deposit level is enough: the levers act on the assessment, so which funding
     * PERMUTATION raised the deposit does not change what an annuity then does. Taking the first of
     * each keeps the candidate count sane.
     */
    const seenLevels = new Set<string>();
    const bases = scenarios.filter((s) => {
        const name = s.name || '';
        if (name === 'Do Nothing') return true;
        // ", Keep Home" is absent where the client has no home (9 Oct 2026).
        const match = /^Pay (50|75|100)% RAD(?:, Keep Home)?/.exec(name);
        if (!match) return false;
        if (seenLevels.has(match[1])) return false;
        seenLevels.add(match[1]);
        return true;
    });
    if (bases.length === 0) return;

    const layered: RadFundingScenario[] = [];

    for (const base of bases) {
        const headroom = Math.max(0, liquid - committedToRad(base));
        if (headroom <= 0) continue;

        // Rebuild the available list net of what the deposit has already taken, so the funding plan
        // for the lever cannot draw the same dollars twice.
        const spent = committedToRad(base);
        let toDeduct = spent;
        const remainingAssets: AvailableSuggestionAsset[] = [];
        for (const asset of assets) {
            const take = Math.min(toDeduct, asset.value);
            toDeduct -= take;
            const left = asset.value - take;
            if (left > 0) remainingAssets.push({ ...asset, value: left });
        }

        const add = (suffix: string, strategy: MeansTestStrategySpec) => {
            layered.push({
                ...base,
                name: `${base.name} + ${suffix}`,
                /*
                 * A group collapses to its best-by-legacy member, so a groupKey shared by two
                 * candidates HIDES one of them. Keyed by the whole spec, not just the lever type:
                 * keyed by type alone, all eight annuity candidates on a base — four sizes times two
                 * residual-capital variants — collapsed to one, which threw away the very trade-off
                 * they exist to show.
                 */
                // Every type its own slot (guaranteed income review E7, 21 Oct 2026): keyed without the type and the
                // indexation, income only hid the rising version, and money back and the full-amount-back type hid
                // each other, so the client might never see the type that protects the family.
                groupKey: `${base.groupKey ?? base.name}-${strategy.type}-${strategy.purchaseAmount ?? strategy.amount ?? 0}-${strategy.residualCapital ?? ''}-${strategy.annuityType ?? ''}-${(strategy as { indexation?: boolean }).indexation ? 'indexed' : ''}`,
                meansTestStrategy: strategy,
            });
        };

        // The client's own answer decides whether an annuity is offered at all, and which versions.
        const annuityPreference = annuityPreferenceOf(factFind as never);
        /*
         * WHOSE ANNUITY (guaranteed income review E8, 21 Oct 2026): the person going into care. It was always the
         * client on the file, so where the partner was the one entering care it was priced, means-tested and
         * judged on age and health on the spouse staying at home. Both entering: the client, as before.
         */
        const forWhom = (factFind.goals as { aged_care_for?: string } | undefined)?.aged_care_for;
        const annuityOwner: 'client' | 'partner' = isPartnered && forWhom === 'partner' ? 'partner' : 'client';
        // A paid deposit is not counted for the Age Pension, so what it spends leaves the count too.
        const sizes = annuitySizesFor(factFind, { investable: headroom, capPct: AC_ANNUITY_SHARE_CAP_PCT, homeowner: Number(factFind?.home?.value) > 0, lessCounted: headroom + spent });
        for (const size of sizes) {
            const purchaseAmount = size.pensionMax ? size.amount : Math.floor(size.amount / 10_000) * 10_000;
            if (purchaseAmount < MIN_ANNUITY_CANDIDATE) continue;
            if (purchaseAmount > headroom) continue;
            const fundingSources = planFunding(remainingAssets, purchaseAmount);
            if (fundingSources.length === 0) continue;
            for (const variant of ANNUITY_RCV_VARIANTS) {
                const allowed = variant.annuityType === 'guaranteed'
                    ? allowsGuaranteed(annuityPreference, factFind as never, annuityOwner)
                    : allowsResidual(annuityPreference, variant.residualCapital);
                if (!allowed) continue;
                add(`$${Math.round(purchaseAmount / 1000)}k lifetime annuity (${variant.label}${size.pensionMax ? ', the amount that lifts the Age Pension most' : ''})`, {
                    type: 'annuity',
                    purchaseAmount,
                    residualCapital: variant.residualCapital,
                    ...(variant.annuityType ? { annuityType: variant.annuityType } : {}),
                    ...(variant.indexation ? { indexation: true } : {}),
                    ownership: annuityOwner,
                    incomeOverride: quotedIncomeFor(annuityIncomeOverride, annuityQuoteKindOf({ annuityType: variant.annuityType, residualCapital: variant.residualCapital, indexation: variant.indexation }), purchaseAmount),
                    fundingSources,
                    rationale: variant.annuityType === 'guaranteed'
                        ? 'A guaranteed income for life combined with life cover. Only the income part is counted at 60% for the Age Pension '
                          + 'and the aged care means test; the life cover is counted in full, so the pension and fee savings are smaller. '
                          + `The money is hard to get back out. ${variant.note}`
                        : 'Centrelink counts only 60% of what a lifetime annuity costs as an asset, and 60% of its payments as income, '
                          + 'so this takes about 40 cents in every dollar out of both the Age Pension test and the aged care means test. '
                          + `The money is hard to get back out. ${variant.note}`,
                });
            }
        }

        for (const perPerson of FUNERAL_BOND_AMOUNTS) {
            const amount = Math.round(isPartnered ? perPerson * 2 : perPerson);
            if (amount > headroom) continue;
            const fundingSources = planFunding(remainingAssets, amount);
            if (fundingSources.length === 0) continue;
            add(`$${Math.round(amount / 1000)}k funeral bond${isPartnered ? 's' : ''}`, {
                type: 'funeralBonds',
                amount,
                ownership: 'client',
                fundingSources,
                rationale:
                    `A funeral bond is exempt from the assets test up to $${FUNERAL_BOND_LIMIT.toLocaleString('en-AU')} per person, `
                    + 'so this money stops being counted while still being spent on something the family would arrange anyway.',
            });
        }

        /*
         * Gifting stays INSIDE the yearly free area. Above it the excess is counted, and deemed, for
         * five years under the deprivation rules — a real cost the engine cannot weigh against a
         * family's own reasons for making the gift, so it does not recommend one.
         */
        const giftAmount = GIFTING_LIMIT_ANNUAL;
        if (giftAmount <= headroom) {
            const fundingSources = planFunding(remainingAssets, giftAmount);
            if (fundingSources.length > 0) {
                add(`$${Math.round(giftAmount / 1000)}k gift within the free area`, {
                    type: 'gifting',
                    amount: giftAmount,
                    ownership: 'client',
                    fundingSources,
                    rationale:
                        `Gifts up to $${GIFTING_LIMIT_ANNUAL.toLocaleString('en-AU')} a year leave the means test straight away. `
                        + 'Anything above that is still counted, and still deemed to earn income, for five years — so this stays '
                        + 'inside the free area.',
                });
            }
        }
    }

    scenarios.push(...layered);
}
